import { useState, useEffect, useMemo } from 'react';
import React from 'react';
import {
  FormControl, Typography, TextField, InputLabel, MenuItem,
  Container, Select, Paper, Grid, Box, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider, Chip,
  Checkbox, Tooltip, Badge
} from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import InfoIcon from "@mui/icons-material/Info";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// ─────────────────────────────────────────────
// Info data for the dialog
// ─────────────────────────────────────────────
const feeDuesInfoSections = [
  {
    title: "Filters",
    color: "#1976D2",
    description: "Use the dropdowns and search fields to filter the fee dues list by session, class, month, section, and father's name.",
    fields: [
      "Session – Academic year filter (auto-selects current year)",
      "Class – Filter by student class",
      "Month – Filter dues by a specific month (select 'All Months' to see grouped view)",
      "Section – Filter by class section (loaded based on selected class)",
      "Father's Name – Autocomplete search to filter by parent name",
    ],
  },
  {
    title: "Table Columns",
    color: "#7B1FA2",
    description: "The dues table displays the following columns for each student record.",
    fields: [
      "Invoice No", "Admission No", "Student Name", "Class", "Roll No",
      "Mobile No", "Fee Entered On", "Month", "Total Fee", "Paid Amount", "Outstanding Balance",
    ],
  },
  {
    title: "All Months View (Grouped)",
    color: "#00897B",
    description: "When 'All Months' is selected, rows are grouped by student. Click 'View Months' to open a modal showing dues for each month with totals.",
    fields: [
      "One row per student — no repeated rows",
      "Month count badge shows how many months have dues",
      "Month-wise modal — select/deselect months to see filtered totals",
      "Total outstanding balance is summed across selected months",
      "Individual month rows with full charge details",
    ],
  },
  {
    title: "Row Selection",
    color: "#388E3C",
    description: "Select one or more student rows using checkboxes to perform bulk actions.",
    fields: [
      "Select All checkbox in header – selects/deselects all rows",
      "Individual row checkbox – select specific students",
      "Selected rows are highlighted and tracked for messaging",
    ],
  },
  {
    title: "Send Dues Message",
    color: "#F57C00",
    description: "Send a WhatsApp/SMS dues notification to all selected students' parents. Only enabled when at least one row is selected.",
    fields: [
      "Validates mobile number (must be 10 digits)",
      "Validates email format before sending",
      "Sends: Admission No, Student Name, Father's Name, Month, Total Fee, Paid Amount, Outstanding Balance",
      "Button is disabled if no rows are selected",
    ],
  },
  {
    title: "Export Options",
    color: "#0277BD",
    description: "Download the dues data as Excel files for offline use or reporting.",
    fields: [
      "Export All Dues – Downloads all dues records as AllDues.xlsx",
      "Export Current Month Dues – Downloads dues for the current month as CurrentMonthDues.xlsx",
    ],
  },
  {
    title: "Pagination & Reset",
    color: "#C62828",
    description: "Navigate through large lists using pagination controls, or reset all filters at once.",
    fields: [
      "Rows per page: 5, 10, 25, 50",
      "Prev / Next buttons for page navigation",
      "Page count displayed as 'Page X of Y'",
      "RESET – Clears all filters, selections, and resets the table",
    ],
  },
];

const SESSION_MONTH_ORDER = [
  "April", "May", "June", "July", "August", "September",
  "October", "November", "December", "January", "February", "March",
];

// Backend sends some months misspelled — map known variants to the correct name
const MONTH_NAME_ALIASES = {
  janaury: 'january', // backend typo: "Janaury" instead of "January"
};

const normalizeMonthName = (monthName) => {
  const cleaned = (monthName || '').trim().toLowerCase();
  return MONTH_NAME_ALIASES[cleaned] || cleaned;
};

const SESSION_MONTH_ORDER_NORMALIZED = SESSION_MONTH_ORDER.map(normalizeMonthName);

const isFutureSessionMonth = (monthName) => {
  const now = new Date();
  const currentSessionIndex = (now.getMonth() + 12 - 3) % 12; // April=0 ... March=11
  const rowSessionIndex = SESSION_MONTH_ORDER_NORMALIZED.indexOf(normalizeMonthName(monthName));
  if (rowSessionIndex === -1) return false; // unrecognized month name — don't filter it out
  return rowSessionIndex > currentSessionIndex;
};      

// ─────────────────────────────────────────────
// Month-wise Dues Modal
// ─────────────────────────────────────────────
const MonthDuesModal = ({ open, onClose, studentRows }) => {
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [chargeOpen, setChargeOpen] = useState(false);
  const [chargeRow, setChargeRow] = useState(null);

  // Initialize: select all months by default
  useEffect(() => {
    if (open && studentRows?.length) {
      setSelectedMonths(studentRows.map((_, i) => i));
    }
  }, [open, studentRows]);

  if (!studentRows || !studentRows.length) return null;

  const student = studentRows[0];

  const toggleMonth = (idx) => {
    setSelectedMonths((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };

  const toggleAll = () => {
    if (selectedMonths.length === studentRows.length) {
      setSelectedMonths([]);
    } else {
      setSelectedMonths(studentRows.map((_, i) => i));
    }
  };

  const selectedData = studentRows.filter((_, i) => selectedMonths.includes(i));
  const totalFee = selectedData.reduce((sum, r) => sum + (parseFloat(r.totalFee) || 0), 0);
  const totalPaid = selectedData.reduce((sum, r) => sum + (parseFloat(r.paidAmount) || 0), 0);
  const totalOutstanding = selectedData.reduce((sum, r) => sum + (parseFloat(r.outstandingBalance) || 0), 0);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ style: { borderRadius: 16, overflow: 'hidden' } }}
      >
        {/* Header */}
        <DialogTitle
          style={{
            backgroundColor: '#0B1F40',
            color: '#fff',
            padding: '18px 24px',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Box style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <CalendarMonthIcon style={{ fontSize: 20 }} />
              <Typography variant="h6" style={{ fontWeight: 600, color: '#fff', fontSize: '1rem' }}>
                Month-wise Dues
              </Typography>
              <Box
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  color: '#fff',
                  fontSize: 11,
                  padding: '2px 10px',
                  borderRadius: 20,
                  fontWeight: 400,
                }}
              >
                {studentRows.length} month{studentRows.length !== 1 ? 's' : ''}
              </Box>
            </Box>
            <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>
              {student.studentName}&nbsp;·&nbsp;{student.admissionNo}&nbsp;·&nbsp;{student.className}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent style={{ padding: 0, backgroundColor: '#f5f5f5' }}>
          {/* Student info strip */}
          <Box style={{ backgroundColor: '#fff', borderBottom: '1px solid #e0e0e0' }}>
            <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
              {[
                { label: 'Father\'s Name', value: student.fathersName || '—' },
                { label: 'Mobile', value: student.mobileNo || '—' },
                { label: 'Roll No.', value: student.rollNo || '—' },
              ].map((item, idx, arr) => (
                <Box
                  key={item.label}
                  style={{
                    padding: '10px 16px',
                    borderRight: idx < arr.length - 1 ? '1px solid #e0e0e0' : 'none',
                  }}
                >
                  <Typography variant="caption" style={{ color: '#888', display: 'block', fontSize: 11 }}>
                    {item.label}
                  </Typography>
                  <Typography variant="body2" style={{ fontWeight: 600, color: '#0B1F40', fontSize: 13 }}>
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Month selection hint */}
            <Typography variant="caption" style={{ color: '#888', fontSize: 12 }}>
              ✓ Select months below to include them in the total calculation.
            </Typography>

            {/* Months table */}
            <Box
              style={{
                backgroundColor: '#fff',
                borderRadius: 12,
                border: '1px solid #e0e0e0',
                overflow: 'hidden',
              }}
            >
              <Box
                style={{
                  backgroundColor: '#EEF2FF',
                  padding: '10px 16px',
                  borderBottom: '1px solid #e0e0e0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  variant="subtitle2"
                  style={{ fontWeight: 600, color: '#1976D2', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}
                >
                  Monthly Dues Breakdown
                </Typography>
                <Button
                  size="small"
                  onClick={toggleAll}
                  style={{ fontSize: 11, color: '#1976D2', textTransform: 'none', padding: '2px 8px' }}
                >
                  {selectedMonths.length === studentRows.length ? 'Deselect All' : 'Select All'}
                </Button>
              </Box>

              <Table size="small">
                <TableHead>
                  <TableRow style={{ backgroundColor: '#fafafa' }}>
                    <TableCell padding="checkbox" style={{ width: 44 }}>
                      <Checkbox
                        size="small"
                        checked={selectedMonths.length === studentRows.length && studentRows.length > 0}
                        indeterminate={selectedMonths.length > 0 && selectedMonths.length < studentRows.length}
                        onChange={toggleAll}
                      />
                    </TableCell>
                    <TableCell style={{ fontWeight: 600, color: '#555', fontSize: '0.78rem' }}>Month</TableCell>
                    <TableCell style={{ fontWeight: 600, color: '#555', fontSize: '0.78rem' }}>Invoice No</TableCell>
                    <TableCell align="right" style={{ fontWeight: 600, color: '#555', fontSize: '0.78rem' }}>Total Fee (₹)</TableCell>
                    <TableCell align="right" style={{ fontWeight: 600, color: '#555', fontSize: '0.78rem' }}>Concession (₹)</TableCell>
                    <TableCell align="right" style={{ fontWeight: 600, color: '#555', fontSize: '0.78rem' }}>Paid (₹)</TableCell>
                    <TableCell align="right" style={{ fontWeight: 600, color: '#C62828', fontSize: '0.78rem' }}>Outstanding (₹)</TableCell>
                    <TableCell align="center" style={{ fontWeight: 600, color: '#555', fontSize: '0.78rem' }}>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {studentRows.map((row, i) => {
                    const isSelected = selectedMonths.includes(i);
                    return (
                      <TableRow
                        key={i}
                        style={{
                          backgroundColor: isSelected ? '#F0F7FF' : i % 2 === 0 ? '#fff' : '#fafafa',
                          transition: 'background 0.15s',
                          opacity: isSelected ? 1 : 0.55,
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={isSelected}
                            onChange={() => toggleMonth(i)}
                          />
                        </TableCell>
                        <TableCell style={{ fontSize: '0.88rem', fontWeight: isSelected ? 600 : 400, color: '#0B1F40' }}>
                          {row.month}
                        </TableCell>
                        <TableCell style={{ fontSize: '0.82rem', color: '#555' }}>{row.invoiceNo || '—'}</TableCell>
                        <TableCell align="right" style={{ fontSize: '0.88rem', color: '#0B1F40' }}>
                          {row.totalFee != null ? Number(row.totalFee).toLocaleString('en-IN') : '—'}
                        </TableCell>
                        <TableCell align="right" style={{ fontSize: '0.88rem', color: '#F57C00' }}>
  {row.concessionAmmount != null ? Number(row.concessionAmmount).toLocaleString('en-IN') : '—'}
</TableCell>
                        <TableCell align="right" style={{ fontSize: '0.88rem', color: '#388E3C' }}>
                          {row.paidAmount != null ? Number(row.paidAmount).toLocaleString('en-IN') : '—'}
                        </TableCell>
                        <TableCell
                          align="right"
                          style={{
                            fontSize: '0.88rem',
                            fontWeight: 600,
                            color: parseFloat(row.outstandingBalance) > 0 ? '#C62828' : '#388E3C',
                          }}
                        >
                          {row.outstandingBalance != null ? Number(row.outstandingBalance).toLocaleString('en-IN') : '—'}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => { setChargeRow(row); setChargeOpen(true); }}
                            style={{ fontSize: 11, padding: '2px 10px', textTransform: 'none' }}
                          >
                            Charges
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>

            {/* Total summary */}
            <Box
              style={{
                backgroundColor: '#fff',
                borderRadius: 12,
                border: '1px solid #e0e0e0',
                overflow: 'hidden',
              }}
            >
              <Box style={{ backgroundColor: '#E8F5E9', padding: '10px 16px', borderBottom: '1px solid #e0e0e0' }}>
                <Typography
                  variant="subtitle2"
                  style={{ fontWeight: 600, color: '#388E3C', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}
                >
                  Selected Months Summary ({selectedMonths.length} of {studentRows.length} months)
                </Typography>
              </Box>
              <Box style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  { label: 'Total Fee', value: totalFee, color: '#0B1F40' },
                  { label: 'Total Paid', value: totalPaid, color: '#388E3C' },
                ].map((item) => (
                  <Box
                    key={item.label}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <Typography variant="body2" style={{ color: '#555', fontSize: 13 }}>{item.label}</Typography>
                    <Typography variant="body2" style={{ fontWeight: 500, color: item.color, fontSize: 13 }}>
                      ₹ {item.value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                ))}
                <Divider style={{ marginTop: 2 }} />
                <Box
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: totalOutstanding > 0 ? '#FFEBEE' : '#E8F5E9',
                    borderRadius: 8,
                    padding: '10px 12px',
                    marginTop: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    style={{ fontWeight: 700, fontSize: 14, color: totalOutstanding > 0 ? '#C62828' : '#2E7D32' }}
                  >
                    Total Outstanding Balance
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ fontWeight: 700, fontSize: 18, color: totalOutstanding > 0 ? '#C62828' : '#2E7D32' }}
                  >
                    ₹ {totalOutstanding.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          style={{
            padding: '12px 20px',
            backgroundColor: '#fff',
            borderTop: '1px solid #e0e0e0',
            gap: 8,
          }}
        >
          <Button
            onClick={() => window.print()}
            variant="outlined"
            style={{ borderRadius: 20, paddingInline: 24, borderColor: '#ccc', color: '#555', fontSize: 13 }}
          >
            Print
          </Button>
          <Button
            onClick={onClose}
            variant="contained"
            style={{ backgroundColor: '#0B1F40', color: '#fff', borderRadius: 20, paddingInline: 28, fontSize: 13 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Nested charge detail modal */}
      <ChargeDetailModal
        open={chargeOpen}
        onClose={() => setChargeOpen(false)}
        row={chargeRow}
      />
    </>
  );
};

// ─────────────────────────────────────────────
// Charge Detail Modal (extracted for reuse)
// ─────────────────────────────────────────────
const ChargeDetailModal = ({ open, onClose, row: selectedChargeRow }) => {
  if (!selectedChargeRow) return null;

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString.replace(
        /(\w+) (\d+) (\d+) (\d+):(\d+)(AM|PM)/,
        (_, month, day, year, hour, minute, period) => {
          const monthIndex = new Date(Date.parse(`${month} 1, 2026`)).getMonth();
          const hour24 = period === 'PM' && hour !== '12'
            ? parseInt(hour) + 12
            : period === 'AM' && hour === '12' ? 0 : parseInt(hour);
          return `${year}-${String(monthIndex + 1).padStart(2, '0')}-${day.padStart(2, '0')}T${String(hour24).padStart(2, '0')}:${minute}:00`;
        }
      ));
      return isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return '—';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ style: { borderRadius: 16, overflow: 'hidden' } }}
    >
      <DialogTitle
        style={{
          backgroundColor: '#0B1F40',
          color: '#fff',
          padding: '18px 24px',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <Typography variant="h6" style={{ fontWeight: 500, color: '#fff', fontSize: '1rem' }}>
              Fee Charge Details
            </Typography>
            {selectedChargeRow?.invoiceNo && (
              <Box style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 11, padding: '2px 10px', borderRadius: 20 }}>
                {selectedChargeRow.invoiceNo}
              </Box>
            )}
          </Box>
          <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>
            {selectedChargeRow?.studentName}&nbsp;·&nbsp;{selectedChargeRow?.admissionNo}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent style={{ padding: 0, backgroundColor: '#f5f5f5' }}>
        {/* Student Info Strip */}
        <Box style={{ backgroundColor: '#fff', borderBottom: '1px solid #e0e0e0' }}>
          <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)' }}>
            {[
              { label: 'Class / Section', value: `${selectedChargeRow?.className || '—'} — ${selectedChargeRow?.sectionName || '—'}` },
              { label: 'Roll No.', value: selectedChargeRow?.rollNo },
              { label: 'Month', value: selectedChargeRow?.month },
              { label: 'Generated On', value: formatDate(selectedChargeRow?.feeGeneratedOn) },
            ].map((item, idx, arr) => (
              <Box key={item.label} style={{ padding: '10px 16px', borderRight: idx < arr.length - 1 ? '1px solid #e0e0e0' : 'none' }}>
                <Typography variant="caption" style={{ color: '#888', display: 'block', fontSize: 11 }}>{item.label}</Typography>
                <Typography variant="body2" style={{ fontWeight: 600, color: '#0B1F40', fontSize: 13 }}>{item.value || '—'}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Charge Breakup */}
          <Box style={{ backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
            <Box style={{ backgroundColor: '#EEF2FF', padding: '10px 16px', borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle2" style={{ fontWeight: 600, color: '#1976D2', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Charge Breakup
              </Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow style={{ backgroundColor: '#fafafa' }}>
                  <TableCell style={{ fontWeight: 500, color: '#888', fontSize: '0.72rem', width: 36 }}>#</TableCell>
                  <TableCell style={{ fontWeight: 500, color: '#888', fontSize: '0.72rem' }}>Charge Head</TableCell>
                  <TableCell align="right" style={{ fontWeight: 500, color: '#888', fontSize: '0.72rem' }}>Amount (₹)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedChargeRow?.chargeBreakup
                  ? selectedChargeRow.chargeBreakup.split(',').map((item, i) => {
                      const [label, value] = item.split('=').map((s) => s.trim());
                      return (
                        <TableRow key={i} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                          <TableCell style={{ color: '#aaa', fontSize: '0.82rem' }}>{i + 1}</TableCell>
                          <TableCell style={{ fontSize: '0.88rem', color: '#222' }}>{label}</TableCell>
                          <TableCell align="right" style={{ fontSize: '0.88rem', fontWeight: 500, color: '#0B1F40' }}>{value}</TableCell>
                        </TableRow>
                      );
                    })
                  : (
                    <TableRow>
                      <TableCell colSpan={3} align="center" style={{ color: '#aaa', fontSize: '0.85rem' }}>
                        No charge breakup available
                      </TableCell>
                    </TableRow>
                  )}
                {selectedChargeRow?.totalChargeAmount > 0 && (
                  <TableRow style={{ backgroundColor: '#EEF2FF' }}>
                    <TableCell colSpan={2} style={{ fontWeight: 600, color: '#1976D2', fontSize: '0.88rem' }}>Total Charge Amount</TableCell>
                    <TableCell align="right" style={{ fontWeight: 600, color: '#1976D2', fontSize: '0.88rem' }}>₹ {selectedChargeRow?.totalChargeAmount}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>

          {/* Payment Summary */}
          <Box style={{ backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
            <Box style={{ backgroundColor: '#E8F5E9', padding: '10px 16px', borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle2" style={{ fontWeight: 600, color: '#388E3C', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Payment Summary
              </Typography>
            </Box>
            <Box style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                { label: 'Total Fee', value: selectedChargeRow?.totalFee, color: '#0B1F40' },
                { label: 'Paid Amount', value: selectedChargeRow?.paidAmount, color: '#388E3C' },
                { label: 'Concession', value: selectedChargeRow?.concessionAmmount, color: '#F57C00' },
              ].map((item) => (
                <Box key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" style={{ color: '#555', fontSize: 13 }}>{item.label}</Typography>
                  <Typography variant="body2" style={{ fontWeight: 500, color: item.color, fontSize: 13 }}>₹ {item.value}</Typography>
                </Box>
              ))}
              <Divider style={{ marginTop: 2 }} />
              <Box
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  backgroundColor: selectedChargeRow?.outstandingBalance > 0 ? '#FFEBEE' : '#E8F5E9',
                  borderRadius: 8, padding: '10px 12px', marginTop: 2,
                }}
              >
                <Typography variant="body2" style={{ fontWeight: 600, fontSize: 14, color: selectedChargeRow?.outstandingBalance > 0 ? '#C62828' : '#2E7D32' }}>
                  Outstanding Balance
                </Typography>
                <Typography variant="body2" style={{ fontWeight: 600, fontSize: 16, color: selectedChargeRow?.outstandingBalance > 0 ? '#C62828' : '#2E7D32' }}>
                  ₹ {selectedChargeRow?.outstandingBalance}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Contact & Parent Info */}
          <Box style={{ backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
            <Box style={{ backgroundColor: '#fafafa', padding: '10px 16px', borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle2" style={{ fontWeight: 600, color: '#555', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Contact & Parent Info
              </Typography>
            </Box>
            <Box style={{ padding: '12px 16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Box>
                <Typography variant="caption" style={{ color: '#888', display: 'block', fontSize: 11 }}>Father's Name</Typography>
                <Typography variant="body2" style={{ fontWeight: 500, color: '#0B1F40', fontSize: 13 }}>{selectedChargeRow?.fathersName || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" style={{ color: '#888', display: 'block', fontSize: 11 }}>Mobile</Typography>
                <Typography variant="body2" style={{ fontWeight: 500, color: '#0B1F40', fontSize: 13 }}>{selectedChargeRow?.mobileNo || '—'}</Typography>
              </Box>
              <Box style={{ gridColumn: '1 / -1' }}>
                <Typography variant="caption" style={{ color: '#888', display: 'block', fontSize: 11 }}>Email</Typography>
                <Typography variant="body2" style={{ fontWeight: 500, color: '#1976D2', fontSize: 13 }}>{selectedChargeRow?.emailId || '—'}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions style={{ padding: '12px 20px', backgroundColor: '#fff', borderTop: '1px solid #e0e0e0', gap: 8 }}>
        <Button onClick={() => window.print()} variant="outlined" style={{ borderRadius: 20, paddingInline: 24, borderColor: '#ccc', color: '#555', fontSize: 13 }}>
          Print
        </Button>
        <Button onClick={onClose} variant="contained" style={{ backgroundColor: '#0B1F40', color: '#fff', borderRadius: 20, paddingInline: 28, fontSize: 13 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
const FeeDuesDetails = () => {
  const [chargeModalOpen, setChargeModalOpen] = useState(false);
  const [selectedChargeRow, setSelectedChargeRow] = useState(null);
  const [monthModalOpen, setMonthModalOpen] = useState(false);
  const [selectedStudentRows, setSelectedStudentRows] = useState([]);
  const [fathersName, setFathersName] = useState(null);
  const [fathersNames, setFathersNames] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [infoOpen, setInfoOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [isLoading, setIsLoading] = useState(false);
  const [noData, setNoData] = useState(false);
  const [classRoom, setClassRoom] = useState('0');
  const [month, setMonth] = useState('0');
  const [section, setSection] = useState('0');
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [months, setMonths] = useState([]);
  const [ddlSession, setDdlSession] = useState([]);
  const [selectedSession, setSelectedSession] = useState('');
  const [rows, setRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  // Determine if "All Months" is currently selected
  const isAllMonths = month === '-1';

  // Group rows by admissionNo when All Months is selected
  const groupedByStudent = useMemo(() => {
    if (!isAllMonths) return null;
    const map = new Map();
    rows
      .filter((row) => !isFutureSessionMonth(row.month))
      .forEach((row) => {
        const key = row.admissionNo;
        if (!map.has(key)) {
          map.set(key, []);
        }
        map.get(key).push(row);
      });
    return Array.from(map.values()).map((studentRows) =>
      [...studentRows].sort(
        (a, b) => SESSION_MONTH_ORDER.indexOf(a.month) - SESSION_MONTH_ORDER.indexOf(b.month)
      )
    );
  }, [rows, isAllMonths]); 

  // For All Months view, display rows are grouped (one per student)
  // For single month, display rows are flat
  const displayRows = useMemo(() => {
    if (isAllMonths && groupedByStudent) {
      // Apply father's name filter on grouped data
      return groupedByStudent.filter(
        (studentRows) => !fathersName || studentRows[0].fathersName === fathersName
      );
    }
    return rows.filter((row) => !fathersName || row.fathersName === fathersName);
  }, [isAllMonths, groupedByStudent, rows, fathersName]);

  const paginatedData = displayRows.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  // Select all (uses flat index into paginatedData)
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedRows(displayRows.map((_, index) => index));
    } else {
      setSelectedRows([]);
    }
  };

  const handleOpenChargeModal = (row) => {
    setSelectedChargeRow(row);
    setChargeModalOpen(true);
  };

  const handleOpenMonthModal = (studentRows) => {
    setSelectedStudentRows(studentRows);
    setMonthModalOpen(true);
  };

  const handleRowSelect = (event, index) => {
    if (event.target.checked) {
      setSelectedRows((prev) => [...prev, index]);
    } else {
      setSelectedRows((prev) => prev.filter((id) => id !== index));
    }
  };

  const isRowSelected = (index) => selectedRows.includes(index);

  const callApiForSelectedRows = async () => {
    try {
      const apiUrl = 'https://wsmsv1.aidigitalsec.com/api/FeeDeposit/SendDuesWtpMsg';
      const token = sessionStorage.getItem('token');

      let requestData;

      if (isAllMonths) {
        // For All Months grouped view, send all months for each selected student
        requestData = selectedRows.flatMap((index) => {
          const studentRows = displayRows[index];
          return studentRows.map((row) => {
            const isValidMobile = /^\d{10}$/.test(row.mobileNo);
            const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.emailId);
            return {
              isChecked: true,
              admissionNo: row.admissionNo,
              studentName: row.studentName,
              fathersName: row.fathersName || '',
              mobileNo: isValidMobile ? row.mobileNo : '',
              emailId: isValidEmail ? row.emailId : '',
              month: row.month,
              totalFee: row.totalFee,
              paidAmount: row.paidAmount,
              outstandingBalance: row.outstandingBalance,
            };
          });
        });
      } else {
        requestData = selectedRows.map((index) => {
          const row = displayRows[index];
          const isValidMobile = /^\d{10}$/.test(row.mobileNo);
          const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.emailId);
          return {
            isChecked: true,
            admissionNo: row.admissionNo,
            studentName: row.studentName,
            fathersName: row.fathersName || '',
            mobileNo: isValidMobile ? row.mobileNo : '',
            emailId: isValidEmail ? row.emailId : '',
            month: row.month,
            totalFee: row.totalFee,
            paidAmount: row.paidAmount,
            outstandingBalance: row.outstandingBalance,
          };
        });
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error processing rows: ${response.status}`);
      }

      setSnackbarMessage('Message sent successfully for all selected students!');
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      setSelectedRows([]);
    } catch (error) {
      console.error('Error calling API:', error);
      setSnackbarMessage('Failed to send messages. Please try again.');
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  useEffect(() => {
    fetchClasses();
    fetchMonths();
    fetchFinancialYears();
  }, []);

  useEffect(() => {
    fetchTableData();
  }, [classRoom, section, month, selectedSession]);

  useEffect(() => {
    if (month !== '0' || classRoom !== '0' || section !== '0' || selectedSession !== '') {
      setRows([]);
    }
  }, [month, classRoom, section, selectedSession]);

  // Reset page when filters/data changes
  useEffect(() => {
    setPage(1);
    setSelectedRows([]);
  }, [month, classRoom, section, selectedSession, fathersName]);

  const fetchFinancialYears = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${apiUrl}/ClassPromotion/GetFinancialYear`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({}),
      });
      if (!response.ok) throw new Error(`Error: ${response.status}`);
      const data = await response.json();
      if (data.data === null && data.msg === "Record Not Found") throw new Error("Record Not Found");
      setDdlSession(data);
     const now = new Date();
      const currentYear = now.getFullYear();
      const sessionStartYear = now.getMonth() < 3 ? currentYear - 1 : currentYear; // Jan–Mar belongs to previous session
      const selectedYear = data.find(year => year.finanacialYear.includes(`${sessionStartYear}-${sessionStartYear + 1}`));
      if (selectedYear) setSelectedSession(selectedYear.financialYearID);
    } catch (error) {
      console.error(error);
    }
  };

  const handleResetFields = () => {
    setClassRoom('0');
    setSection('0');
    setMonth('0');
    setFathersName(null);
    setSections([]);
    setRows([]);
    setSelectedRows([]);
    setNoData(false);
  };

  const fetchClasses = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${apiUrl}/Enquiry/ddlClassName`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: sessionStorage.getItem('token') },
        body: JSON.stringify({}),
      });
      const data = await response.json();
      if (data.data === null && data.msg === "Record Not Found") throw new Error("Record Not Found");
      setClasses(data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const fetchSections = async (classId) => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${apiUrl}/Teacher/ddlSection_clsId`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: sessionStorage.getItem('token') },
        body: JSON.stringify({ teacherId: sessionStorage.getItem('employeeId'), classId }),
      });
      const data = await response.json();
      setSections(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching sections:', error);
    }
  };

  const fetchMonths = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${apiUrl}/Fine/GetMonthList`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: sessionStorage.getItem('token') },
      });
      const data = await response.json();
      console.log('Month list:', data.map(m => JSON.stringify(m.month)));
      setMonths(data);
      const currentMonthName = new Date().toLocaleString('default', { month: 'long' });
      const currentMonth = data.find(m => m.month.toLowerCase() === currentMonthName.toLowerCase());
      if (currentMonth) setMonth(currentMonth.monthId);
    } catch (error) {
      console.error('Error fetching months:', error);
    }
  };

  const fetchTableData = async () => {
    try {
      setIsLoading(true);
      setNoData(false);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${apiUrl}/FeeDeposit/AllDues`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: sessionStorage.getItem('token') },
        body: JSON.stringify({ classId: classRoom, sectionId: section, monthId: month, sessionId: selectedSession }),
      });
      if (!response.ok) throw new Error(`Error: ${response.status}`);
      const data = await response.json();
      if (!data || !data.length) {
        setRows([]);
        setNoData(true);
            } else {
        console.log('Sample months:', data.map(r => JSON.stringify(r.month)));
        setRows(data);
        setNoData(false);
        const uniqueFathersNames = [...new Set(data.map(row => row.fathersName).filter(name => name))];
        setFathersNames(uniqueFathersNames);
      }
    } catch (error) {
      console.error('Error fetching table data:', error);
      setNoData(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClassChange = (event) => {
    const selectedClassId = event.target.value;
    setClassRoom(selectedClassId);
    setSection('0');
    fetchSections(selectedClassId);
  };

  const handleMonthChange = (event) => { setMonth(event.target.value); };
  const handleSectionChange = (event) => { setSection(event.target.value); };
  const handleSessionChange = (event) => { setSelectedSession(event.target.value); };

  const downloadFile = async (url, filename) => {
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: sessionStorage.getItem('token') },
        body: JSON.stringify({ classId: classRoom, sectionId: section, monthId: month, sessionId: selectedSession }),
      });
      if (!response.ok) throw new Error(`Error: ${response.status}`);
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const handleExportAllDues = () => downloadFile('https://wsmsv1.aidigitalsec.com/api/FeeDeposit/DownloadExcel_DuesAll', 'AllDues.xlsx');
  const handleExportCurrentMonthDues = () => downloadFile('https://wsmsv1.aidigitalsec.com/api/FeeDeposit/DownloadExcel_CurrentMonth', 'CurrentMonthDues.xlsx');

  const handleChangePage = (event, newPage) => { setPage(newPage); };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  // ── Render table rows ──
  const renderTableBody = () => {
    if (isLoading) {
      return (
        <TableRow>
          <TableCell colSpan={isAllMonths ? 9 : 10} align="center">Loading...</TableCell>
        </TableRow>
      );
    }
    if (noData) {
      return (
        <TableRow>
          <TableCell colSpan={isAllMonths ? 9 : 10} align="center">No Record Found</TableCell>
        </TableRow>
      );
    }

    if (isAllMonths) {
      // Grouped view: one row per student
      return paginatedData.map((studentRows, index) => {
        const student = studentRows[0];
        const totalOutstanding = studentRows.reduce((sum, r) => sum + (parseFloat(r.outstandingBalance) || 0), 0);
        const globalIndex = (page - 1) * rowsPerPage + index;

        return (
          <TableRow
            key={student.admissionNo}
            style={{
              backgroundColor: isRowSelected(globalIndex) ? '#E3F2FD' : index % 2 === 0 ? '#fff' : '#fafafa',
              transition: 'background 0.15s',
            }}
          >
            <TableCell padding="checkbox">
              <input
                type="checkbox"
                checked={isRowSelected(globalIndex)}
                onChange={(event) => handleRowSelect(event, globalIndex)}
              />
            </TableCell>
            <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>{student.admissionNo}</TableCell>
            <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem', fontWeight: 600 }}>{student.studentName}</TableCell>
            <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>{student.className}</TableCell>
            <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>{student.rollNo}</TableCell>
            <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>{student.mobileNo}</TableCell>
            <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>{student.fathersName}</TableCell>
            {/* Month count badge */}
            <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip
                  label={`${studentRows.length} month${studentRows.length !== 1 ? 's' : ''}`}
                  size="small"
                  style={{
                    backgroundColor: '#EEF2FF',
                    color: '#1976D2',
                    fontWeight: 600,
                    fontSize: 12,
                  }}
                />
              </Box>
            </TableCell>
            {/* Total outstanding */}
            <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>
              <Typography
                variant="body2"
                style={{
                  fontWeight: 700,
                  color: totalOutstanding > 0 ? '#C62828' : '#388E3C',
                  fontSize: 14,
                }}
              >
                ₹ {totalOutstanding.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </TableCell>
            {/* View Months button */}
            <TableCell sx={{ borderRight: '1px solid #ccc' }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<CalendarMonthIcon style={{ fontSize: 15 }} />}
                onClick={() => handleOpenMonthModal(studentRows)}
                style={{
                  backgroundColor: '#0B1F40',
                  color: '#fff',
                  fontSize: 12,
                  textTransform: 'none',
                  borderRadius: 20,
                  padding: '4px 14px',
                  whiteSpace: 'nowrap',
                }}
              >
                View Months
              </Button>
            </TableCell>
          </TableRow>
        );
      });
    }

    // Normal single-month view
    return paginatedData.map((row, index) => {
      const globalIndex = (page - 1) * rowsPerPage + index;
      return (
        <TableRow
          key={index}
          style={{
            backgroundColor: isRowSelected(globalIndex) ? '#E3F2FD' : index % 2 === 0 ? '#fff' : '#fafafa',
          }}
        >
          <TableCell padding="checkbox">
            <input
              type="checkbox"
              checked={isRowSelected(globalIndex)}
              onChange={(event) => handleRowSelect(event, globalIndex)}
            />
          </TableCell>
          <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>{row.invoiceNo}</TableCell>
          <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>{row.admissionNo}</TableCell>
          <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>{row.studentName}</TableCell>
          <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>{row.className}</TableCell>
          <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>{row.rollNo}</TableCell>
          <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>{row.mobileNo}</TableCell>
          <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>{row.feeGeneratedOn}</TableCell>
          <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>{row.month}</TableCell>
          <TableCell sx={{ borderRight: '1px solid #ccc' }}>
            <Button variant="outlined" size="small" onClick={() => handleOpenChargeModal(row)}>
              View Details
            </Button>
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <Container maxWidth={false}>
      {/* ── Header ── */}
      <Box
        sx={{
          backgroundColor: '#0B1F40',
          padding: '12px 24px',
          boxShadow: 3,
          borderRadius: '4px 4px 0 0',
          margin: '2px auto',
          maxWidth: "100%",
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h4" sx={{ color: '#fff', textAlign: 'center', fontWeight: 'bold', flexGrow: 1 }}>
          Fee Dues List
        </Typography>
        <IconButton
          onClick={() => setInfoOpen(true)}
          title="Page Information"
          style={{
            backgroundColor: "rgba(255,255,255,0.15)",
            border: "2px solid rgba(255,255,255,0.6)",
            borderRadius: "50%",
            padding: "6px",
            color: "#fff",
          }}
        >
          <InfoIcon style={{ fontSize: 26 }} />
        </IconButton>
      </Box>

      {/* ── Info Dialog ── */}
      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} maxWidth="md" fullWidth PaperProps={{ style: { borderRadius: 16, padding: 0 } }}>
        <DialogTitle
          style={{
            backgroundColor: "#0B1F40", color: "#fff", display: "flex", alignItems: "center",
            justifyContent: "space-between", padding: "16px 24px",
          }}
        >
          <Box display="flex" alignItems="center">
            <InfoIcon style={{ marginRight: 8 }} />
            <Typography variant="h6" style={{ fontWeight: "bold" }}>Fee Dues List — Page Guide</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers style={{ backgroundColor: "#f9f9f9", padding: "24px" }}>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
            This page displays outstanding fee dues for students. Use the filters to narrow down results, select students to send dues notifications, and export reports as needed.
          </Typography>
          {feeDuesInfoSections.map((sec, index) => (
            <Box key={index} style={{ backgroundColor: "#fff", borderRadius: 12, padding: "18px 20px", marginBottom: 16, borderLeft: `5px solid ${sec.color}`, boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
              <Typography variant="h6" style={{ fontWeight: "bold", color: sec.color, marginBottom: 6 }}>{sec.title}</Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 10 }}>{sec.description}</Typography>
              <Divider style={{ marginBottom: 10 }} />
              <Typography variant="caption" style={{ fontWeight: "bold", color: "#555", display: "block", marginBottom: 6 }}>📝 DETAILS</Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {sec.fields.map((field, i) => (
                  <Chip key={i} label={field} size="small" variant="outlined" style={{ borderColor: sec.color, color: sec.color }} />
                ))}
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px", backgroundColor: "#f9f9f9" }}>
          <Button onClick={() => setInfoOpen(false)} variant="contained" style={{ backgroundColor: "#0B1F40", color: "#fff", borderRadius: 20, paddingInline: 28 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Main Content ── */}
      <Box>
        <Paper elevation={3} sx={{ padding: 3, maxWidth: "100%", margin: 'auto', marginTop: 2 }}>
          {/* Filters */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="teacher-label">Session</InputLabel>
                <Select labelId="teacher-label" label="Session" value={selectedSession} onChange={handleSessionChange}>
                  {ddlSession.map((year) => (
                    <MenuItem key={year.financialYearID} value={year.financialYearID}>{year.finanacialYear}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="class-label">Class</InputLabel>
                <Select labelId="class-label" value={classRoom} label="Class" onChange={handleClassChange}>
                  <MenuItem value="0"><em>Select</em></MenuItem>
                  {classes.map((classItem) => (
                    <MenuItem key={classItem.classId} value={classItem.classId}>{classItem.className}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="month-label">Month</InputLabel>
                <Select labelId="month-label" value={month} label="Month" onChange={handleMonthChange}>
                  <MenuItem value="0"><em>Select</em></MenuItem>
                  <MenuItem value="-1">
                    <Box display="flex" alignItems="center" gap={1}>
                      <CalendarMonthIcon style={{ fontSize: 16, color: '#00897B' }} />
                      <span style={{ color: '#00897B', fontWeight: 600 }}>All Months (Grouped)</span>
                    </Box>
                  </MenuItem>
                {months.map((monthItem) => (
                    <MenuItem
                      key={monthItem.monthId}
                      value={monthItem.monthId}
                      disabled={isFutureSessionMonth(monthItem.month)}
                    >
                      {monthItem.month}
                      {isFutureSessionMonth(monthItem.month) && (
                        <span style={{ marginLeft: 8, fontSize: 11, color: '#999' }}>(Upcoming)</span>
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="section-label">Section</InputLabel>
                <Select labelId="section-label" value={section} label="Section" onChange={handleSectionChange}>
                  <MenuItem value="0"><em>Select</em></MenuItem>
                  {sections.map((sectionItem) => (
                    <MenuItem key={sectionItem.sectionId} value={sectionItem.sectionId}>{sectionItem.sectionName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={fathersNames}
                value={fathersName}
                onChange={(event, newValue) => setFathersName(newValue || null)}
                renderInput={(params) => <TextField {...params} label="Father's Name" variant="outlined" fullWidth />}
                freeSolo
                clearOnEscape
                noOptionsText="No matching father's names"
              />
            </Grid>
          </Grid>

          {/* All Months info banner */}
          {isAllMonths && (
            <Box
              sx={{
                mt: 2,
                p: '10px 16px',
                backgroundColor: '#E0F2F1',
                borderRadius: 2,
                border: '1px solid #80CBC4',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <CalendarMonthIcon style={{ fontSize: 18, color: '#00897B' }} />
              <Typography variant="body2" style={{ color: '#00695C', fontWeight: 500, fontSize: 13 }}>
                <strong>Grouped View:</strong> Each row shows one student. Click <strong>View Months</strong> to see month-wise dues and select months to calculate the total outstanding balance.
              </Typography>
            </Box>
          )}

          {/* Action buttons */}
          <Box display="flex" justifyContent="space-between" marginTop={2} gap={2} marginBottom={4}>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={handleResetFields}
              sx={{ width: "80px", height: "50px", padding: "10px 0", fontWeight: 'bold', mt: 2 }}
            >
              RESET
            </Button>
            <Box display="flex" gap={2} flexWrap="wrap" mt={2}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={callApiForSelectedRows}
                disabled={selectedRows.length === 0}
                sx={{ width: "160px", height: "50px", padding: "10px 0", fontWeight: 'bold' }}
              >
                Send Dues Message
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleExportAllDues}
                sx={{ width: "160px", height: "50px", padding: "10px 0", fontWeight: 'bold' }}
              >
                Export Unpaid Records
              </Button>
              <Button
                variant="contained"
                color="secondary"
                size="small"
                onClick={handleExportCurrentMonthDues}
                sx={{ width: "160px", height: "50px", padding: "10px 0", fontWeight: 'bold' }}
              >
                Export Current Month Dues
              </Button>
            </Box>
          </Box>

          {/* Table */}
          <TableContainer
            component={Paper}
            style={{ marginTop: "20px", width: '100%', margin: "auto", borderRadius: 2, border: '1px solid #ccc' }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <input
                      type="checkbox"
                      checked={selectedRows.length === displayRows.length && displayRows.length > 0}
                      onChange={handleSelectAll}
                    />
                  </TableCell>

                  {isAllMonths ? (
                    // Grouped view headers
                    <>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontSize: '1.1rem' }}>Admission No</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontSize: '1.1rem' }}>Student Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontSize: '1.1rem' }}>Class</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontSize: '1.1rem' }}>Roll No</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontSize: '1.1rem' }}>Mobile No</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontSize: '1.1rem' }}>Father's Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontSize: '1.1rem' }}>Months</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontSize: '1.1rem', color: '#C62828' }}>Total Outstanding</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontSize: '1.1rem' }}>Month-wise Dues</TableCell>
                    </>
                  ) : (
                    // Single month headers
                    <>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontSize: '1.1rem' }}>Invoice No</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontSize: '1.1rem' }}>Admission No</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontSize: '1.1rem' }}>Student Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontSize: '1.1rem' }}>Class</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontSize: '1.1rem' }}>Roll No</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontSize: '1.1rem' }}>Mobile No</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontSize: '1.1rem' }}>Fee Entered On</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontSize: '1.1rem' }}>Month</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontSize: '1.1rem' }}>Charge Details</TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {renderTableBody()}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {displayRows.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
              <TextField
                select
                label="Rows per page"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                sx={{ width: 150 }}
                SelectProps={{ native: true }}
              >
                {rowsPerPageOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </TextField>
              <Box>
                <Typography variant="body2" sx={{ display: 'inline-block', marginRight: 2 }}>
                  Page {page} of {Math.ceil(displayRows.length / rowsPerPage)}
                </Typography>
                <Button variant="outlined" onClick={() => handleChangePage(null, page - 1)} disabled={page === 1} sx={{ mr: 1 }}>
                  Prev
                </Button>
                <Button variant="outlined" onClick={() => handleChangePage(null, page + 1)} disabled={page >= Math.ceil(displayRows.length / rowsPerPage)}>
                  Next
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>

      {/* ── Snackbar ── */}
      <Snackbar open={openSnackbar} autoHideDuration={4000} onClose={() => setOpenSnackbar(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* ── Month-wise Dues Modal (All Months view) ── */}
      <MonthDuesModal
        open={monthModalOpen}
        onClose={() => setMonthModalOpen(false)}
        studentRows={selectedStudentRows}
      />

      {/* ── Single Charge Detail Modal (normal view) ── */}
      <ChargeDetailModal
        open={chargeModalOpen}
        onClose={() => setChargeModalOpen(false)}
        row={selectedChargeRow}
      />
    </Container>
  );
};

export default FeeDuesDetails;